import React from 'react';
import { D, st, toArr, fmt, fmtDate, apiGet, apiPost, apiDel } from '../constants';

const fmtHrs = d => (d > 0 ? `+${d}` : `${d}`) + ' hrs';
const fmtMoney = d => (d > 0 ? '+' : d < 0 ? '−' : '') + fmt(Math.abs(d));

const MODEL_COSTS = {
  "claude-sonnet-4-6":        { in: 3.00,  out: 15.00 },
  "claude-sonnet-4-20250514": { in: 3.00,  out: 15.00 },
  "claude-haiku-4-5-20251001":{ in: 0.80,  out: 4.00  },
};
const calcCost = (model, inp, out) => {
  const r = MODEL_COSTS[model] || MODEL_COSTS["claude-sonnet-4-6"];
  return (inp/1_000_000)*r.in + (out/1_000_000)*r.out;
};

function AiUsageSection() {
  const [usage, setUsage] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [period, setPeriod] = React.useState("30");

  React.useEffect(() => {
    setLoading(true); setError(null);
    apiGet(`/ai/usage?days=${period}`)
      .then(res => setUsage(toArr(res)))
      .catch(() => { setError("Usage logging not yet enabled on backend."); setUsage([]); })
      .finally(() => setLoading(false));
  }, [period]);

  const totalCost = usage.reduce((s,u)=>s+calcCost(u.model,u.inputTokens||0,u.outputTokens||0),0);
  const totalTokens = usage.reduce((s,u)=>s+(u.inputTokens||0)+(u.outputTokens||0),0);

  return (
    <div style={st.card}>
      <div style={{display:"flex",gap:8,marginBottom:20}}>
        {[["7","7d"],["30","30d"],["90","90d"]].map(([v,l])=>(
          <button key={v} style={{...st.btn(),marginLeft:0,background:period===v?D.accent:D.border,color:period===v?"#fff":D.text}} onClick={()=>setPeriod(v)}>{l}</button>
        ))}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:20}}>
        {[[`Est. Cost (${period}d)`,"$"+totalCost.toFixed(4),D.warning],["API Calls",usage.length,D.accent],["Total Tokens",((totalTokens)/1000).toFixed(1)+"K",D.success]].map(([l,v,c],i)=>(
          <div key={i} style={st.stat}><div style={{fontSize:11,color:D.muted,marginBottom:6}}>{l}</div><div style={{fontSize:20,fontWeight:600,color:c}}>{v}</div></div>
        ))}
      </div>
      <div style={{...st.card,background:D.surface,marginBottom:16}}>
        <div style={{fontSize:12,fontWeight:500,color:D.muted,marginBottom:8}}>📋 Pricing Reference</div>
        <div style={{fontSize:12,color:D.muted}}>Sonnet: <span style={{color:D.text}}>$3/M in · $15/M out</span> &nbsp;|&nbsp; Haiku: <span style={{color:D.text}}>$0.80/M in · $4/M out</span></div>
        <div style={{fontSize:11,color:D.muted,marginTop:6}}>💡 Typical AI bid ≈ <span style={{color:D.success,fontWeight:600}}>$0.014/call</span> with Sonnet</div>
      </div>
      {loading && <div style={{textAlign:"center",padding:24,color:D.muted}}>Loading…</div>}
      {error && <div style={{padding:"12px 16px",background:D.warning+"11",border:`1px solid ${D.warning}33`,borderRadius:8,color:D.warning,fontSize:13}}>{error}</div>}
      {!loading && usage.length>0 && (
        <div style={{overflowX:"auto"}}><table style={st.table}>
          <thead><tr>{["Date","Model","Input","Output","Cost"].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
          <tbody>{usage.slice(0,50).map((u,i)=>(
            <tr key={i}>
              <td style={{...st.td,color:D.muted,fontSize:12}}>{fmtDate((u.createdAt||"").slice(0,10))}</td>
              <td style={{...st.td,fontSize:11}}>{(u.model||"").replace("claude-","")}</td>
              <td style={{...st.td,color:D.muted,fontSize:12}}>{((u.inputTokens||0)/1000).toFixed(1)}K</td>
              <td style={{...st.td,color:D.muted,fontSize:12}}>{((u.outputTokens||0)/1000).toFixed(1)}K</td>
              <td style={{...st.td,color:D.warning,fontWeight:500}}>${calcCost(u.model,u.inputTokens||0,u.outputTokens||0).toFixed(4)}</td>
            </tr>
          ))}</tbody>
        </table></div>
      )}
      <div style={{marginTop:16,textAlign:"center"}}>
        <a href="https://console.anthropic.com/settings/usage" target="_blank" rel="noreferrer" style={{color:D.accent,fontSize:13}}>→ Anthropic Console ↗</a>
      </div>
    </div>
  );
}

function SiteAnalytics() {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    apiGet("/analytics/summary").then(setData).catch(e=>setError(e.message));
  }, []);

  if (error) return <div style={st.card}><div style={{color:D.danger}}>Failed to load analytics: {error}</div></div>;
  if (!data) return <div style={{color:D.muted}}>Loading…</div>;

  const stepLabels = ["Company Info","Owner Account","Payment Methods","Rates & Markup","Business Profile","Done"];
  const step1Count = data.funnel[0]?.count || 0;

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:16}}>
        <div style={st.card}>
          <div style={{color:D.muted,fontSize:12}}>Landing views (total)</div>
          <div style={{fontSize:24,fontWeight:600}}>{data.landingViews.total}</div>
          <div style={{color:D.muted,fontSize:11}}>{data.landingViews.last7d} last 7d · {data.landingViews.last30d} last 30d</div>
        </div>
        <div style={st.card}>
          <div style={{color:D.muted,fontSize:12}}>Total signups</div>
          <div style={{fontSize:24,fontWeight:600}}>{data.conversion.totalSignups}</div>
        </div>
        <div style={st.card}>
          <div style={{color:D.muted,fontSize:12}}>Paid conversions</div>
          <div style={{fontSize:24,fontWeight:600}}>{data.conversion.paidCount}</div>
        </div>
        <div style={st.card}>
          <div style={{color:D.muted,fontSize:12}}>Trial → paid rate</div>
          <div style={{fontSize:24,fontWeight:600}}>{data.conversion.rate}%</div>
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
        <div style={st.card}>
          <div style={{fontWeight:600,marginBottom:10,fontSize:13}}>Signup wizard funnel</div>
          <table style={st.table}>
            <thead><tr>{["Step","Reached","Retained"].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.funnel.map((f,i)=>
                <tr key={f.step}>
                  <td style={st.td}>{f.step}. {stepLabels[i]}</td>
                  <td style={st.td}>{f.count}</td>
                  <td style={st.td}>{step1Count ? Math.round((f.count/step1Count)*100) : 0}%</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div style={st.card}>
          <div style={{fontWeight:600,marginBottom:10,fontSize:13}}>Companies by plan</div>
          <table style={st.table}>
            <thead><tr>{["Plan","Count"].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
            <tbody>
              {data.companiesByPlan.map(p=>
                <tr key={p.plan}>
                  <td style={st.td}>{p.plan}</td>
                  <td style={st.td}>{p.count}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CompanyAdmin() {
  const [companies, setCompanies] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [bonusInputs, setBonusInputs] = React.useState({});
  const [busyId, setBusyId] = React.useState(null);

  const load = () => {
    apiGet("/admin/companies").then(r => setCompanies(toArr(r.companies))).catch(e=>setError(e.message));
  };
  React.useEffect(load, []);

  const toggleWhitelist = async (c) => {
    setBusyId(c.id);
    try {
      const r = await apiPost(`/admin/companies/${c.id}/beta-whitelist`, { whitelisted: !c.aiBetaWhitelisted });
      setCompanies(prev => prev.map(x => x.id === c.id ? {...x, aiBetaWhitelisted: r.aiBetaWhitelisted} : x));
    } catch (e) { setError(e.message); }
    setBusyId(null);
  };

  const grantBonus = async (c) => {
    const amount = parseInt(bonusInputs[c.id] || "0", 10);
    if (!amount) return;
    setBusyId(c.id);
    try {
      const r = await apiPost(`/admin/companies/${c.id}/ai-bonus`, { amount });
      setCompanies(prev => prev.map(x => x.id === c.id ? {...x, aiBidBonus: r.aiBidBonus} : x));
      setBonusInputs(prev => ({...prev, [c.id]: ""}));
    } catch (e) { setError(e.message); }
    setBusyId(null);
  };

  if (error) return <div style={st.card}><div style={{color:D.danger}}>Failed to load companies: {error}</div></div>;
  if (!companies) return <div style={{color:D.muted}}>Loading…</div>;

  return (
    <div style={st.card}>
      {companies.length === 0
        ? <div style={{color:D.muted,padding:"30px 0",textAlign:"center"}}>No companies found.</div>
        : <div style={{overflowX:"auto"}}><table style={st.table}>
            <thead>
              <tr>{["Company","Email","Plan","AI bids used","Bonus","Beta whitelist","Grant bonus bids"].map(h=>
                <th key={h} style={st.th}>{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {companies.map(c=>
                <tr key={c.id}>
                  <td style={st.td}>{c.name}{c.isTestAccount ? <span style={{color:D.muted,fontSize:11,marginLeft:6}}>(test)</span> : null}</td>
                  <td style={st.td}>{c.email}</td>
                  <td style={st.td}>{c.plan}{c.planStatus ? <span style={{color:D.muted}}> · {c.planStatus}</span> : null}</td>
                  <td style={st.td}>{c.aiBidUsed}</td>
                  <td style={st.td}>{c.aiBidBonus}</td>
                  <td style={st.td}>
                    <button
                      disabled={busyId===c.id}
                      onClick={()=>toggleWhitelist(c)}
                      style={{
                        cursor:busyId===c.id?"default":"pointer",
                        border:`1px solid ${c.aiBetaWhitelisted ? D.success : D.muted}66`,
                        background:c.aiBetaWhitelisted ? D.success+"22" : "transparent",
                        color:c.aiBetaWhitelisted ? D.success : D.muted,
                        borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:600
                      }}
                    >
                      {c.aiBetaWhitelisted ? "Whitelisted ✓" : "Not whitelisted"}
                    </button>
                  </td>
                  <td style={st.td}>
                    <div style={{display:"flex",gap:6,alignItems:"center"}}>
                      <input
                        type="number"
                        min="1"
                        placeholder="amount"
                        value={bonusInputs[c.id] || ""}
                        onChange={e=>setBonusInputs(prev=>({...prev,[c.id]:e.target.value}))}
                        style={{width:70,...st.input,padding:"3px 6px",fontSize:12}}
                      />
                      <button
                        disabled={busyId===c.id || !bonusInputs[c.id]}
                        onClick={()=>grantBonus(c)}
                        style={{
                          cursor:(busyId===c.id || !bonusInputs[c.id])?"default":"pointer",
                          border:`1px solid ${D.accent}66`,background:D.accent+"22",color:D.accent,
                          borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:600
                        }}
                      >
                        Add
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table></div>
      }
    </div>
  );
}

function FeedbackAdmin() {
  const [items, setItems] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busyId, setBusyId] = React.useState(null);

  const load = () => {
    apiGet("/admin/feedback").then(r => setItems(toArr(r.feedback))).catch(e=>setError(e.message));
  };
  React.useEffect(load, []);

  const dismiss = async (id) => {
    setBusyId(id);
    try {
      await apiDel(`/admin/feedback/${id}`);
      setItems(prev => prev.filter(f => f.id !== id));
    } catch (e) { setError(e.message); }
    setBusyId(null);
  };

  if (error) return <div style={st.card}><div style={{color:D.danger}}>Failed to load feedback: {error}</div></div>;
  if (!items) return <div style={{color:D.muted}}>Loading…</div>;

  return (
    <div style={st.card}>
      {items.length === 0
        ? <div style={{color:D.muted,padding:"30px 0",textAlign:"center"}}>No feedback submitted yet.</div>
        : <div style={{overflowX:"auto"}}><table style={st.table}>
            <thead>
              <tr>{["Date","Company","User","Page","Message",""].map(h=>
                <th key={h} style={st.th}>{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {items.map(f=>
                <tr key={f.id}>
                  <td style={st.td}>{new Date(f.createdAt).toLocaleString()}</td>
                  <td style={st.td}>{f.companyName}</td>
                  <td style={st.td}>{f.userName}{f.userEmail ? <span style={{color:D.muted,fontSize:11}}><br/>{f.userEmail}</span> : null}</td>
                  <td style={st.td}>{f.page}</td>
                  <td style={{...st.td,maxWidth:360,whiteSpace:"pre-wrap"}}>{f.message}</td>
                  <td style={st.td}>
                    <button disabled={busyId===f.id} onClick={()=>dismiss(f.id)} style={{...st.btn(),padding:"3px 10px",fontSize:11}}>Dismiss</button>
                  </td>
                </tr>
              )}
            </tbody>
          </table></div>
      }
    </div>
  );
}

function AnthropicCredits() {
  const [status, setStatus] = React.useState(null);
  const [input, setInput] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const load = () => { apiGet('/ai/admin/anthropic-status').then(setStatus).catch(()=>{}); };
  React.useEffect(load, []);

  const save = async () => {
    const val = parseFloat(input);
    if (isNaN(val) || val < 0) return;
    setSaving(true);
    await apiPost('/ai/admin/anthropic-balance', { balance: val });
    setSaving(false);
    setInput('');
    load();
  };

  if (!status) return null;

  const pingColor = ({ok:D.success, no_credits:D.danger, api_error:D.warning})[status.pingStatus] || D.muted;
  const pingLabel = ({ok:'✓ API online', no_credits:'✗ No credits — add now', api_error:'⚠ API error', unknown:'— Not checked yet'})[status.pingStatus] || '—';

  return (
    <div style={{...st.card, border:'1px solid '+(status.lowBalance ? D.danger+'55' : D.border)}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <span style={{fontWeight:600,fontSize:13}}>Anthropic Credits</span>
        <span style={{color:pingColor,fontSize:12}}>{pingLabel}</span>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:14}}>
        <div style={st.stat}>
          <div style={{fontSize:11,color:D.muted,marginBottom:4}}>Stored Balance</div>
          <div style={{fontSize:20,fontWeight:700}}>{status.storedBalance !== null ? '$'+status.storedBalance.toFixed(2) : '—'}</div>
        </div>
        <div style={st.stat}>
          <div style={{fontSize:11,color:D.muted,marginBottom:4}}>Total Spend</div>
          <div style={{fontSize:20,fontWeight:700,color:D.warning}}>{'$'+status.totalSpend.toFixed(4)}</div>
        </div>
        <div style={st.stat}>
          <div style={{fontSize:11,color:D.muted,marginBottom:4}}>Est. Remaining</div>
          <div style={{fontSize:20,fontWeight:700,color:status.lowBalance ? D.danger : D.success}}>
            {status.estimatedRemaining !== null ? '$'+status.estimatedRemaining.toFixed(2) : '—'}
          </div>
        </div>
      </div>
      {status.lowBalance && (
        <div style={{background:D.danger+'22',border:'1px solid '+D.danger+'44',borderRadius:8,padding:'6px 12px',fontSize:12,color:D.danger,marginBottom:12}}>
          ⚠ Balance below $5 — top up soon
        </div>
      )}
      <div style={{display:'flex',gap:8,alignItems:'center'}}>
        <input
          type="number" min="0" step="0.01" placeholder="Enter balance after top-up"
          value={input} onChange={e=>setInput(e.target.value)}
          onKeyDown={e=>e.key==='Enter'&&save()}
          style={{flex:1,...st.input,fontSize:13}}
        />
        <button onClick={save} disabled={saving} style={{...st.btn(),marginLeft:0}}>
          {saving ? '…' : 'Update'}
        </button>
        <a href="https://console.anthropic.com/settings/billing" target="_blank" rel="noreferrer"
          style={{color:D.accent,fontSize:12,whiteSpace:'nowrap'}}>+ Add credits ↗</a>
      </div>
      {status.pingAt && (
        <div style={{color:D.muted,fontSize:10,marginTop:8}}>Last ping: {new Date(status.pingAt+'Z').toLocaleString()}</div>
      )}
    </div>
  );
}

export default function AiAccuracy() {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    apiGet("/admin/ai-calibration").then(setData).catch(e=>setError(e.message));
  }, []);

  if (error) return <div style={st.card}><div style={{color:D.danger}}>Failed to load: {error}</div></div>;
  if (!data) return <div style={{color:D.muted}}>Loading…</div>;

  const bids = toArr(data.bids);

  return (
    <div>
      <div style={{...st.h1, marginBottom:8}}>Site Analytics</div>
      <div style={{color:D.muted, marginBottom:12, fontSize:13, maxWidth:720}}>
        Landing page traffic, signup-wizard drop-off by step, and trial-to-paid conversion across all companies.
      </div>
      <SiteAnalytics/>

      <div style={{...st.h1, marginBottom:8, marginTop:32}}>AI Usage & Cost</div>
      <div style={{color:D.muted, marginBottom:12, fontSize:13, maxWidth:720}}>
        Anthropic API usage and estimated cost across all companies.
      </div>
      <AiUsageSection/>

      <div style={{...st.h1, marginBottom:8, marginTop:32}}>Anthropic Credits</div>
      <div style={{color:D.muted, marginBottom:12, fontSize:13, maxWidth:720}}>
        Track your Anthropic credit balance against actual spend. Update the balance after each top-up.
      </div>
      <AnthropicCredits/>

      <div style={{...st.h1, marginBottom:8, marginTop:32}}>Beta Feedback</div>
      <div style={{color:D.muted, marginBottom:12, fontSize:13, maxWidth:720}}>
        Feedback submitted by users via the in-app Feedback button.
      </div>
      <FeedbackAdmin/>

      <div style={{...st.h1, marginBottom:8, marginTop:32}}>AI Accuracy</div>
      <div style={{color:D.muted, marginBottom:20, fontSize:13, maxWidth:720}}>
        Compares each AI Voice Bid's original AI-generated proposal against the final bid the contractor saved.
        Tracks how far the AI's labor-hour and materials-cost estimates land from real-world numbers, across all contractors —
        used to refine AI estimating accuracy over time.
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16,marginBottom:20}}>
        <div style={st.card}>
          <div style={{color:D.muted,fontSize:12}}>AI bids tracked</div>
          <div style={{fontSize:24,fontWeight:600}}>{data.count}</div>
        </div>
        <div style={st.card}>
          <div style={{color:D.muted,fontSize:12}}>Avg labor-hours delta (final − AI)</div>
          <div style={{fontSize:24,fontWeight:600}}>{fmtHrs(data.avgHoursDelta)}</div>
        </div>
        <div style={st.card}>
          <div style={{color:D.muted,fontSize:12}}>Avg materials-cost delta (final − AI)</div>
          <div style={{fontSize:24,fontWeight:600}}>{fmtMoney(data.avgMatCostDelta)}</div>
        </div>
      </div>

      <div style={st.card}>
        {bids.length === 0
          ? <div style={{color:D.muted,padding:"30px 0",textAlign:"center"}}>
              No AI-generated bids tracked yet — data will appear here as contractors save AI Voice Bids going forward.
            </div>
          : <div style={{overflowX:"auto"}}><table style={st.table}>
              <thead>
                <tr>{["Company","Bid #","Description","AI hrs → Final hrs","Δ hrs","AI materials → Final","Δ materials","Status"].map(h=>
                  <th key={h} style={st.th}>{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {bids.map(b=>
                  <tr key={b.id}>
                    <td style={st.td}>{b.company}</td>
                    <td style={st.td}><span style={{color:D.accent,fontWeight:500}}>{b.number}</span></td>
                    <td style={{...st.td,maxWidth:260,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{b.description}</td>
                    <td style={st.td}>{b.aiHours} → {b.finalHours}</td>
                    <td style={{...st.td,fontWeight:500}}>{fmtHrs(b.hoursDelta)}</td>
                    <td style={st.td}>{fmt(b.aiMatCost)} → {fmt(b.finalMatCost)}</td>
                    <td style={{...st.td,fontWeight:500}}>{fmtMoney(b.matCostDelta)}</td>
                    <td style={st.td}>{b.status}</td>
                  </tr>
                )}
              </tbody>
            </table></div>
        }
      </div>

      <div style={{...st.h1, fontSize:16, marginTop:32, marginBottom:8}}>Beta whitelist & bonus AI bids</div>
      <div style={{color:D.muted, marginBottom:12, fontSize:13, maxWidth:720}}>
        Whitelisted companies get unlimited AI bids. Otherwise each company gets 10 free AI bids (trial only) plus any bonus granted here —
        bonuses add to the limit, they don't replace it.
      </div>
      <CompanyAdmin/>
    </div>
  );
}
